import { inject, injectable } from 'inversify';
import { Device, type types } from 'mediasoup-client';
import {
  ConnectionState,
  DtlsParameters,
  IceParameters,
  Transport,
  TransportEvents,
} from 'mediasoup-client/lib/Transport';
import { serializeError } from 'serialize-error';
import {
  FIFTEEN_SECONDS_IN_MS,
  TEN_SECONDS_IN_MS,
  MINUTE_IN_MS,
  SECOND_IN_MS,
  TWO_SECONDS_IN_MS,
} from '../../constants/common';
import {
  CLIENT_EVENTS,
  MEDIASOUP_EVENTS,
  NETWORK_OBSERVER_EVENTS,
} from '../../constants/events';
import { REJOIN_REQUIRED_CONSUMER_ERROR_MESSAGES } from '../../constants/network';
import AlreadyConnectedError from '../../errors/AlreadyConnectedError';
import EnhancedEventEmitter from '../../EnhancedEventEmitter';
import { isExpectedSignalingError } from '../../errors/isExpectedSignalingError';
import NotFoundError from '../../errors/NotFoundError';
import { TOKEN } from '../../inversify.tokens';
import {
  CreateConsumerPayload,
  CreateConsumerResponse,
  PeerResponse,
  ProduceParams,
  ProducerData,
  RemoteConsumerOptions,
  Role,
  TrackLabel as SdkTrackLabel,
  TransportsStateInfo,
  TransportStateInfo,
  UpdatePeerAppDataPayload,
} from '../../types/common';
import { ClientObserverEvents } from '../../types/engine';
import { LDTransport } from '../../types/mediasoup';
import { NetworkObserverEvents } from '../../types/network';
import { CallState } from '../CallState';
import Logger from '../Logger';
import { MyPeer } from '../MyPeer';
import type {
  ConsumerCreateResponse as ApiConsumerCreateResponse,
  TrackLabel as ApiTrackLabel,
  PeerDataProducerLabels,
  PeerItem,
  ProducerItem,
  TProducerAppData,
} from '../signalingApiClient/api';
import SignalingApiClient from '../signalingApiClient/SignalingApiClient';
import DatachannelHeartbeater from './DatachannelHeartbeater';
import DataChannelsManager from './DataChannelsManager';
import SignalingHeartbeater from './SignalingHeartbeater';
import NeedJoinFirstError from '../../errors/NeedJoinFirstError';

const TRANSPORT_RESTART_ICE_INTERVAL = 1_500;
const TRANSPORT_DETAILS_DELAY = 1500;

@injectable()
class Network {
  public sendTransport?: LDTransport;

  public receiveTransport?: LDTransport;

  public heartbeatDataProducer?: types.DataProducer;

  public signalingTransport?: LDTransport;

  private preferRelay?: boolean;

  readonly #iceIntervalTimeouts = new Map<string, ReturnType<typeof setInterval>>();

  readonly #eventEmitter: EnhancedEventEmitter<NetworkObserverEvents>;

  readonly #logger: Logger;

  peersLoaded = false;

  constructor(
    @inject(TOKEN.CallState) private readonly state: CallState,
    @inject(TOKEN.MyPeer) private readonly myPeer: MyPeer,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
    @inject(TOKEN.ClientEventEmitter) private readonly clientEventEmitter: EnhancedEventEmitter<ClientObserverEvents>,
    @inject(TOKEN.DataChannelsManager) private readonly dataChannelsManager: DataChannelsManager,
    @inject(TOKEN.SignalingApiClient) private readonly signalingApiClient: SignalingApiClient,
    @inject(TOKEN.DatachannelHeartbeater) private readonly datachannelHeartbeater: DatachannelHeartbeater,
    @inject(TOKEN.SignalingHeartbeater) private readonly signalingHeartbeater: SignalingHeartbeater,
  ) {
    this.#logger = loggerFactory('Network');
    this.#eventEmitter = new EnhancedEventEmitter();
  }

  get isNetworkActive(): boolean {
    return ['connected', 'new'].includes(this.receiveTransport?.connectionState ?? '')
      && ['connected', 'new'].includes(this.sendTransport?.connectionState ?? '');
  }

  get observer(): EnhancedEventEmitter<NetworkObserverEvents> {
    return this.#eventEmitter;
  }

  private removeEventListeners(): void {
    const { sendTransport, receiveTransport, signalingTransport: connectionTransport } = this;

    if (sendTransport) {
      const eventNames: Array<keyof TransportEvents> = [
        'connect',
        'produce',
        'connectionstatechange',
      ];
      eventNames.forEach((x) => sendTransport.removeAllListeners(x));
    }

    if (receiveTransport) {
      const eventNames: Array<keyof TransportEvents> = [
        'connect',
        'produce',
        'connectionstatechange',
      ];
      eventNames.forEach((x) => receiveTransport.removeAllListeners(x));
    }

    if (connectionTransport) {
      const eventNames: Array<keyof TransportEvents> = [
        'connect',
        'produce',
        'producedata',
        'connectionstatechange',
      ];
      eventNames.forEach((x) => connectionTransport.removeAllListeners(x));
    }
  }

  public reset(): void {
    this.datachannelHeartbeater.dispose();
    this.signalingHeartbeater.dispose();
    this.heartbeatDataProducer = undefined;
    this.peersLoaded = false;
    this.stopRestartIceAttempts();
    this.removeEventListeners();
    this.closeAllDataChannels();
    this.closeSendTransport();
    this.closeReceiveTransport();
    this.closeConnectionTransport();
  }

  async closeRemoteProducer(producerId: string): Promise<void> {
    await this.signalingApiClient.producer.closeProducer(producerId);
  }

  closeSendTransport(): void {
    if (!this.sendTransport) {
      this.#logger.debug('closeSendTransport()', { message: 'No available send transport' });

      return;
    }

    this.closeTransport(this.sendTransport);
    this.sendTransport = undefined;
  }

  closeReceiveTransport(): void {
    if (!this.receiveTransport) {
      this.#logger.debug('closeReceiveTransport()', { message: 'No available receive transport' });

      return;
    }

    this.closeTransport(this.receiveTransport);
    this.receiveTransport = undefined;
  }

  closeConnectionTransport(): void {
    if (!this.signalingTransport) {
      this.#logger.debug('closeConnectionTransport()', { message: 'No available connection transport' });

      return;
    }

    this.closeTransport(this.signalingTransport);
    this.signalingTransport = undefined;
  }

  private closeTransport(transport: LDTransport): void {
    try {
      transport.close();
      this.#logger.debug('closeTransport()', { transportId: transport.id, direction: transport.direction });
    } catch (error) {
      this.#logger.error('closeTransport()', { error, transportId: transport.id, direction: transport.direction });
    }
  }

  async createSendTransport(mediasoupDevice: Device): Promise<LDTransport> {
    if (this.sendTransport) {
      this.#logger.warn('createSendTransport()', { message: 'Send transport already exists, closing previous' });
      this.closeSendTransport();
    }

    const { data: options } = await this.signalingApiClient.transport.createTransport({ localDirection: 'send' });

    const transport = mediasoupDevice.createSendTransport({
      ...options,
      iceTransportPolicy: this.preferRelay ? 'relay' : options.iceTransportPolicy,
    }) as LDTransport;

    this.#logger.debug('createSendTransport()', { transportId: transport.id, direction: transport.direction });
    this.sendTransport = transport;
    this.subscribeCommonTransportEvents(transport);

    return transport;
  }

  async createRecvTransport(mediasoupDevice: Device): Promise<LDTransport> {
    if (this.receiveTransport) {
      this.#logger.warn('createRecvTransport()', { message: 'Receive transport already exists, closing previous' });
      this.closeReceiveTransport();
    }

    const { data: options } = await this.signalingApiClient.transport.createTransport({ localDirection: 'receive' });

    const transport = mediasoupDevice.createRecvTransport({
      ...options,
      iceTransportPolicy: this.preferRelay ? 'relay' : options.iceTransportPolicy,
    }) as LDTransport;

    this.#logger.debug('createReceiveTransport()', { transportId: transport.id, direction: transport.direction });
    this.receiveTransport = transport;
    this.subscribeCommonTransportEvents(transport);

    return transport;
  }

  async createSignalingTransport(mediasoupDevice: Device): Promise<LDTransport> {
    if (this.signalingTransport) {
      this.#logger.warn('createSignalingTransport()',
        { message: 'Signaling transport already exists, closing previous' });
      this.closeConnectionTransport();
    }

    const { data: options } = await this.signalingApiClient.transport.createTransport({
      localDirection: 'receive',
      tcpOnly: true,
    });

    const transport = mediasoupDevice.createSendTransport({
      ...options,
      iceTransportPolicy: this.preferRelay ? 'relay' : options.iceTransportPolicy,
    }) as LDTransport;

    this.#logger.debug('createConnectionTransport()', {
      transportId: transport.id,
      direction: transport.direction,
      tcpOnly: true,
    });
    this.signalingTransport = transport;
    this.subscribeCommonTransportEvents(transport);

    return transport;
  }

  async getIceParameters(transport: LDTransport): Promise<IceParameters> {
    const { data: { iceParameters } } = await this.signalingApiClient.transport.getTransportIceParameters(transport.id);

    return iceParameters;
  }

  async restartIce(transport: LDTransport): Promise<void> {
    try {
      const iceParameters = await this.getIceParameters(transport);
      await transport.restartIce({ iceParameters });
      this.#logger.info('restartIce completed', { iceParameters });
    } catch (error) {
      const logAction = (error instanceof NotFoundError || error instanceof NeedJoinFirstError)
        ? this.#logger.warn.bind(this.#logger)
        : isExpectedSignalingError(error)
          ? this.#logger.debug.bind(this.#logger)
          : this.#logger.error.bind(this.#logger);
      logAction('restartIce() error', { error: serializeError(error) });
    }
  }

  async createConsumer({
    producerId,
    rtpCapabilities,
    producerPeerId,
  }: CreateConsumerPayload): Promise<CreateConsumerResponse> {
    const transport = this.receiveTransport;

    if (!transport) {
      this.#logger.error('createConsumer()', { reason: 'missing receive transport' });
      throw new Error('Failed to create consumer due to missing receive transport');
    }

    this.#logger.debug('createConsumer()', { producerId });
    const consumerOptions = await this.signalingApiClient.consumer.createConsumer({
      producerId,
      transportId: transport.id,
      rtpCapabilities,
      producerPeerId,
      forceCloseExisting: true,
    })
      .then(({ data }) => this.toRemoteConsumerOptions(data))
      .catch((error: unknown) => {
        const logAction = isExpectedSignalingError(error)
          ? this.#logger.debug.bind(this.#logger)
          : this.#logger.error.bind(this.#logger);
        logAction('Failed to get consumer options', {
          producerId,
          producerPeerId,
          error: serializeError(error),
        });
        throw error;
      });

    const consumer = await transport.consume(consumerOptions)
      .catch((error: unknown) => {
        const isRejoinRequired = error instanceof Error
          && REJOIN_REQUIRED_CONSUMER_ERROR_MESSAGES.some((message) => error.message.includes(message));

        if (isRejoinRequired) {
          this.#logger.error('createConsumer() reached irrecoverable mediasoup state', {
            producerId,
            producerPeerId,
            error: serializeError(error),
          });
          this.signalingApiClient.abortPendingRequests('channel-rejoin');
          this.clientEventEmitter.safeEmit(CLIENT_EVENTS.channelRejoinRequired);
        }

        throw error;
      });

    const { producerPaused: isProducerPaused } = consumerOptions;

    if (isProducerPaused && consumer.kind !== 'audio') {
      consumer.pause();
    }

    this.#logger.debug('Consumer created', {
      consumerId: consumer.id,
      label: consumer.appData.label,
      isProducerPaused,
    });

    return {
      consumer,
      isProducerPaused,
    };
  }

  async initializeHeartbeats(): Promise<void> {
    this.signalingHeartbeater.start();

    const transport = this.sendTransport;

    if (!transport) {
      this.#logger.error('createHeartbeatProducer()', { reason: 'missing send transport' });
      throw new Error('Failed to create data producer');
    }

    const dataProducer = await transport.produceData({
      label: 'heart-beater',
      protocol: 'heartbeat',
    });

    this.heartbeatDataProducer = dataProducer;

    dataProducer.once('transportclose', () => {
      this.heartbeatDataProducer?.close();
      this.heartbeatDataProducer = undefined;
      this.datachannelHeartbeater.dispose();
    });

    this.datachannelHeartbeater.init(transport, dataProducer);
  }

  // only for tcp transport connection
  async createKeepAliveConsumer(): Promise<void> {
    const transport = this.signalingTransport;

    if (!transport) {
      this.#logger.error('createKeepAliveConsumer()', { reason: 'missing signaling transport' });

      return;
    }

    const dataProducer = await transport.produceData({
      label: 'consumers-scores',
      protocol: 'keepalive',
    });

    dataProducer.once('open', () => {
      dataProducer.send(new Uint8Array(0));
    });

    dataProducer.once('transportclose', () => {
      dataProducer.close();
    });
  }

  get channelMessagesDataConsumer(): types.DataConsumer | undefined {
    return this.dataChannelsManager.channelMessagesDataConsumer;
  }

  async closeRemoteConsumer(consumerId: string): Promise<void> {
    await this.signalingApiClient.consumer.closeConsumer(consumerId);
  }

  async resumeRemoteConsumer(consumerId: string): Promise<void> {
    await this.signalingApiClient.consumer.resumeConsumer(consumerId);
  }

  async pauseRemoteProducer(producerId: string): Promise<void> {
    await this.signalingApiClient.producer.pauseProducer(producerId);
  }

  async resumeRemoteProducer(producerId: string): Promise<void> {
    await this.signalingApiClient.producer.resumeProducer(producerId);
  }

  async pauseRemoteConsumer(consumerId: string): Promise<void> {
    await this.signalingApiClient.consumer.pauseConsumer(consumerId);
  }

  async updatePeerAppData({ appData }: UpdatePeerAppDataPayload): Promise<void> {
    await this.signalingApiClient.appData.updateSelfPeerAppData({ appData });
  }

  async setProducerMaxSpatialLayer(producerId: string, maxSpatialLayer: number): Promise<void> {
    await this.signalingApiClient.producer.setProducerMaxSpatialLayer(producerId, { maxSpatialLayer });
  }

  async requestProducerMaxSpatialLayer(producerId: string, spatialLayer: number): Promise<void> {
    await this.signalingApiClient.producer.requestProducerMaxSpatialLayer(producerId, { spatialLayer });
  }

  async changePeerProducePermissions(peerId: string, producePermissions?: string[]): Promise<void> {
    await this.signalingApiClient.peers.changePeerProducePermissions(peerId, {
      producePermissions: producePermissions?.map((label) => label as ApiTrackLabel),
    });
  }

  async addPeerProducePermission(peerId: string, label: string): Promise<void> {
    await this.signalingApiClient.peers.addPeerProducePermissions(peerId, label as ApiTrackLabel);
  }

  async removePeerProducePermission(peerId: string, label: string): Promise<void> {
    await this.signalingApiClient.peers.removePeerProducePermissions(peerId, label as ApiTrackLabel);
  }

  async updateConsumerPreferredLayers(consumerId: string, spatialLayer: number, temporalLayer?: number): Promise<void> {
    await this.signalingApiClient.consumer.updateConsumerPreferredLayers(consumerId, {
      spatialLayer,
      temporalLayer,
    });
  }

  async requestConsumerKeyFrame(consumerId: string): Promise<void> {
    await this.signalingApiClient.consumer.requestConsumerKeyFrame(consumerId);
  }

  async ping(): Promise<void> {
    await this.signalingApiClient.ping.pingPeer();
  }

  async getTransportsStateInfo(): Promise<TransportsStateInfo> {
    const [receive, send] = await Promise.all([
      this.getParsedTransportStats(this.receiveTransport),
      this.getParsedTransportStats(this.sendTransport),
    ]);

    return {
      receive,
      send,
    };
  }

  public setPreferRelay(value: boolean): void {
    this.preferRelay = value;
  }

  public getPreferRelay(): boolean {
    return this.preferRelay ?? false;
  }

  public async getChannelPeers(role?: Role): Promise<{ peers: PeerResponse[] }> {
    const { data } = await this.signalingApiClient.peers.getPeers({ role });

    this.peersLoaded = true;

    const peers = (data.items ?? []).map((peer) => this.mapPeerResponse(peer));

    return { peers };
  }

  async createAllDataChannels(): Promise<void> {
    if (!this.receiveTransport) {
      throw new Error('Receive transport is not created');
    }

    await this.dataChannelsManager.createAudioObserver(this.receiveTransport);
    await this.dataChannelsManager.createChannelMessagesDataConsumer(this.receiveTransport);
  }

  closeAllDataChannels(): void {
    this.dataChannelsManager.closeAudioObserver();
    this.dataChannelsManager.closeChannelMessagesDataConsumer();
  }

  async waitForReceiveTransportConnected({
    onReceiveTransportFailed,
  }: { onReceiveTransportFailed: () => Promise<void> }): Promise<void> {
    if (!this.receiveTransport) {
      throw new Error('receiveTransport is not initialized');
    }

    const firstAttemptResult = await this.waitForTransportTerminalState(this.receiveTransport);

    if (firstAttemptResult === 'connected') {
      return;
    }

    await onReceiveTransportFailed();

    // After relay escalation (ICE restart), the transport state changes asynchronously.
    // The state may still be 'failed' from the previous attempt, so we must wait for
    // the ICE restart to take effect before checking for the new terminal state.
    await this.waitForIceRestartStateTransition(this.receiveTransport);

    const secondAttemptResult = await this.waitForTransportTerminalState(this.receiveTransport);

    if (secondAttemptResult === 'connected') {
      return;
    }

    throw new Error(`receiveTransport connection failed after relay escalation: ${ secondAttemptResult }`);
  }

  async triggerChannelStateEvent(peersRole?: Role): Promise<number> {
    const { data } = await this.signalingApiClient.channel.triggerChannelStateEvent({ role: peersRole });

    if (typeof data.messageId !== 'number') {
      throw new Error('Invalid channel state event response');
    }

    return data.messageId;
  }

  async waitForChannelMessagesDataConsumerConnected(): Promise<void> {
    if (!this.dataChannelsManager.channelMessagesDataConsumer) {
      throw new Error('channelMessagesDataConsumer is not initialized');
    }

    await this.waitForDataConsumerConnected(this.dataChannelsManager.channelMessagesDataConsumer);
  }

  private async waitForIceRestartStateTransition(
    transport: LDTransport,
    timeoutAfter: number = TEN_SECONDS_IN_MS,
  ): Promise<void> {
    if (transport.connectionState !== 'failed') {
      return;
    }

    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        cleanup();
        resolve();
      }, timeoutAfter);

      const cleanup = (): void => {
        clearTimeout(timeout);
        transport.off('connectionstatechange', onStateChange);
      };

      const onStateChange = (): void => {
        cleanup();
        resolve();
      };

      transport.on('connectionstatechange', onStateChange);
    });
  }

  private async waitForTransportTerminalState(
    transport: LDTransport,
    timeoutAfter: number = MINUTE_IN_MS,
  ): Promise<ConnectionState> {
    if (transport.connectionState === 'connected') {
      return transport.connectionState;
    }

    if (transport.connectionState === 'failed') {
      return transport.connectionState;
    }

    if (transport.connectionState === 'closed') {
      throw new Error(`receiveTransport connection failed: ${ transport.connectionState }`);
    }

    return await new Promise<ConnectionState>((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        resolve(transport.connectionState);
      }, timeoutAfter);

      const cleanup = (): void => {
        clearTimeout(timeout);
        transport.off('connectionstatechange', onConnectionStateChange);
      };

      const onConnectionStateChange = (): void => {
        if (['connected', 'failed'].includes(transport.connectionState)) {
          cleanup();
          resolve(transport.connectionState);
        } else if (transport.connectionState === 'closed') {
          cleanup();
          reject(new Error(`receiveTransport connection failed: ${ transport.connectionState }`));
        }
      };

      transport.on('connectionstatechange', onConnectionStateChange);
    });
  }

  private async waitForDataConsumerConnected(
    dataConsumer: types.DataConsumer | undefined,
    timeoutAfter: number = MINUTE_IN_MS,
  ): Promise<void> {
    if (!dataConsumer) {
      throw new Error('Data consumer is not initialized');
    }

    if (dataConsumer.readyState === 'open') {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Timed out waiting for data consumer to connect'));
      }, timeoutAfter);

      const cleanup = (): void => {
        clearTimeout(timeout);
        dataConsumer.off('open', onOpen);
        dataConsumer.off('close', onClose);
      };

      const onOpen = (): void => {
        cleanup();
        resolve();
      };

      const onClose = (): void => {
        cleanup();
        reject(new Error(`Data consumer closed unexpectedly: ${ dataConsumer.readyState }`));
      };

      dataConsumer.on('open', onOpen);
      dataConsumer.on('close', onClose);
    });
  }

  private async getParsedTransportStats(transport?: Transport): Promise<TransportStateInfo | undefined> {
    if (!transport) {
      this.#logger.debug('getParsedTransportStats()', { message: 'Transport is missing' });

      return undefined;
    }

    try {
      const pcStats = Array.from((await transport.getStats()).values());
      const localCandidateStats = pcStats.find((item) => item.type === 'local-candidate');
      const remoteCandidateStats = pcStats.find((item) => item.type === 'remote-candidate');
      const transportStats = pcStats.find((item) => item.type === 'transport');

      return {
        dtlsState: transportStats?.dtlsState,
        iceState: transportStats?.iceState,
        dtlsRole: transportStats?.dtlsRole,
        iceRole: transportStats?.iceRole,
        selectedCandidatePairChanges: transportStats?.selectedCandidatePairChanges,
        tlsVersion: transportStats?.tlsVersion,
        localCandidateState: {
          ip: localCandidateStats?.ip,
          address: localCandidateStats?.address,
          port: localCandidateStats?.port,
          protocol: localCandidateStats?.protocol,
          candidateType: localCandidateStats?.candidateType,
        },
        remoteCandidateState: {
          ip: remoteCandidateStats?.ip,
          address: remoteCandidateStats?.address,
          port: remoteCandidateStats?.port,
          protocol: remoteCandidateStats?.protocol,
          candidateType: remoteCandidateStats?.candidateType,
        },
      };
    } catch (error) {
      const logAction = error instanceof Error && error.name === 'InvalidStateError'
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.error.bind(this.#logger);
      logAction('getParsedTransportStats()', { error });

      return undefined;
    }
  }

  private async logTransportDetails(transport: LDTransport): Promise<void> {
    let stats: RTCStatsReport | undefined = undefined;

    try {
      stats = await transport.getStats();
    } catch (error: unknown) {
      this.#logger.error('Failed to log detailed WebRTC transport stats', {
        error: serializeError(error),
        transportId: transport.id,
        reason: 'failed to parse getStats',
      });

      return;
    }

    try {
      const statsArr = [...stats];
      const [, transportStats] = statsArr.find(([, it]) => it?.type === 'transport') ?? [];
      const [, pair] = statsArr.find(
        ([, it]) => it.type === 'candidate-pair' && it.state === 'succeeded' && it.nominated,
      ) ?? [];
      const localCandidate: Record<string, unknown> | undefined = stats.get(String(pair?.localCandidateId ?? '-'));
      const remoteCandidate: Record<string, unknown> | undefined = stats.get(String(pair?.remoteCandidateId ?? '-'));
      const detailedStats: Record<string, unknown> = {
        hasNominatedPair: !!pair,
        transportId: transport.id,
        iceRole: transportStats?.iceRole,
        iceState: transportStats?.iceState,
        dtlsRole: transportStats?.dtlsRole,
        dtlsState: transportStats?.dtlsState,
        local: {
          address: localCandidate?.address,
          candidateType: localCandidate?.candidateType,
          foundation: localCandidate?.foundation,
          networkType: localCandidate?.networkType,
          isRemote: localCandidate?.isRemote,
          port: localCandidate?.port,
          protocol: localCandidate?.protocol,
          relatedAddress: localCandidate?.relatedAddress,
          relatedPort: localCandidate?.relatedPort,
          transportId: localCandidate?.transportId,
          relayProtocol: localCandidate?.relayProtocol,
          url: localCandidate?.url,
        },
        remote: {
          address: remoteCandidate?.address,
          candidateType: remoteCandidate?.candidateType,
          foundation: remoteCandidate?.foundation,
          isRemote: remoteCandidate?.isRemote,
          port: remoteCandidate?.port,
          protocol: remoteCandidate?.protocol,
          transportId: remoteCandidate?.transportId,
        },
      };

      if (pair?.totalRoundTripTime && pair?.responsesReceived) {
        detailedStats.pairRoundTripTime = Math.round((pair.totalRoundTripTime / pair.responsesReceived) * SECOND_IN_MS);
      }

      this.#logger.info('Detailed WebRTC transport stats', detailedStats);
    } catch (error: unknown) {
      this.#logger.error('Failed to log detailed WebRTC transport stats', {
        error: serializeError(error),
        transportId: transport.id,
        reason: '-',
      });
    }
  }

  private stopRestartIceAttempts(): void {
    Array.from(this.#iceIntervalTimeouts.values()).forEach((interval) => clearInterval(interval));
  }

  private toRemoteConsumerOptions(response: ApiConsumerCreateResponse): RemoteConsumerOptions {
    const { producerPaused, ...rest } = response;

    return {
      ...(rest as unknown as RemoteConsumerOptions),
      producerPaused,
    };
  }

  private mapPeerResponse(peer: PeerItem): PeerResponse {
    return {
      id: peer.id,
      channelIds: peer.channelIds ?? [],
      groups: peer.groups ?? [],
      producePermissions: (peer.producePermissions ?? []) as SdkTrackLabel[],
      appId: peer.appId ?? this.state.appId ?? '',
      producers: (peer.producers ?? []).map((producer) => this.mapProducer(producer)),
      uid: peer.uid,
      appData: peer.appData ?? {},
      role: (peer.role ?? 'audience') as Role,
      loginDate: peer.loginDate ? new Date(peer.loginDate) : new Date(),
    };
  }

  private mapProducer(producer: ProducerItem): ProducerData {
    return {
      id: producer.id,
      kind: producer.kind,
      peerId: producer.peerId,
      label: producer.label as SdkTrackLabel,
      paused: producer.paused ?? false,
      encodings: [],
      maxSpatialLayer: 0,
    };
  }

  private async connectTransport(transportId: string, dtlsParameters: DtlsParameters): Promise<void> {
    try {
      await this.signalingApiClient.transport.connectTransport(transportId, { dtlsParameters });
    } catch (error) {
      if (error instanceof AlreadyConnectedError) {
        this.#logger.warn('connectTransport() transport already connected', { transportId });

        return;
      }

      throw error;
    }
  }

  private subscribeCommonTransportEvents(transport: LDTransport): void {
    transport.on('connect', async (
      { dtlsParameters }: { dtlsParameters: DtlsParameters },
      callback: () => void,
      errback: (error: Error) => void,
    ) => {
      try {
        await this.connectTransport(transport.id, dtlsParameters);
        callback();

        this.#logger.debug(`${ transport.direction }Transport.connect()`, {
          transportId: transport.id,
          dtlsRole: dtlsParameters.role,
        });

        setTimeout(async () => await this.logTransportDetails(transport), TWO_SECONDS_IN_MS);
        setTimeout(async () => await this.checkTransportState(transport), FIFTEEN_SECONDS_IN_MS);
      } catch (error) {
        this.#logger.error(`${ transport.direction }Transport.on('connect')`, { error });
        errback(error as Error);
      }
    });

    transport.on('produce', async (
      parameters: ProduceParams,
      callback: ({ id }: { id: string }) => void,
      errback: (error: Error) => void,
    ) => {
      try {
        const { data } = await this.signalingApiClient.transport.produceMediaToTransport(transport.id, {
          kind: parameters.kind,
          rtpParameters: parameters.rtpParameters,
          appData: parameters.appData as TProducerAppData,
          forceCloseExisting: true,
        });
        const producerId = data.id;

        callback({ id: producerId });

        this.#logger.debug(`${ transport.direction }Transport.produce()`, {
          producerId,
          transportId: transport.id,
          kind: parameters.kind,
        });
      } catch (error: unknown) {
        const logAction = isExpectedSignalingError(error)
          ? this.#logger.info.bind(this.#logger)
          : this.#logger.error.bind(this.#logger);
        logAction(`${ transport.direction }Transport.produce()`, {
          error: serializeError(error),
          transportId: transport.id,
          kind: parameters.kind,
        });
        errback(error as Error);
      }
    });

    transport.on('producedata', async (
      parameters,
      callback: ({ id }: { id: string }) => void,
      errback: (error: Error) => void,
    ) => {
      try {
        const { data } = await this.signalingApiClient.transport.produceDataToTransport(transport.id, {
          label: parameters.label as PeerDataProducerLabels,
          sctpStreamParameters: parameters.sctpStreamParameters,
          appData: parameters.appData,
        });

        callback({ id: data.id });

        this.#logger.debug(`${ transport.direction }Transport.produceData()`, {
          producerId: data.id,
          transportId: transport.id,
          sctpStreamParameters: parameters.sctpStreamParameters,
          label: parameters.label,
        });
      } catch (error: unknown) {
        const logAction = isExpectedSignalingError(error)
          ? this.#logger.info.bind(this.#logger)
          : this.#logger.error.bind(this.#logger);
        logAction(`${ transport.direction }Transport.produceData()`, {
          error: serializeError(error),
          transportId: transport.id,
          sctpStreamParameters: parameters.sctpStreamParameters,
          label: parameters.label,
        });
        errback(error as Error);
      }
    });

    transport.on('connectionstatechange', (state: RTCPeerConnectionState) => {
      this.#logger.info(`${ transport.direction }Transport.state`, { state });

      if (['closed', 'connected'].includes(state)) {
        this.#eventEmitter.safeEmit(NETWORK_OBSERVER_EVENTS.transportState, {
          state,
          direction: transport.direction,
        });

        if (this.#iceIntervalTimeouts.has(transport.id)) {
          this.#logger.debug('Stop receiveTransport.restartIce() retries', { recvTransportId: transport.id });
          clearInterval(this.#iceIntervalTimeouts.get(transport.id));
          this.#iceIntervalTimeouts.delete(transport.id);

          if (state === 'connected') {
            this.#eventEmitter.safeEmit(NETWORK_OBSERVER_EVENTS.transportState, {
              state: 'reconnected',
              direction: transport.direction,
            });
          }
        }

        if (state === 'connected') {
          setTimeout(() => {
            this.logTransportDetails(transport).catch((error: unknown) => {
              this.#logger.debug('Failed to log transport details', {
                error: serializeError(error),
              });
            });
          }, TRANSPORT_DETAILS_DELAY);
        }

        return;
      }

      this.#eventEmitter.safeEmit(NETWORK_OBSERVER_EVENTS.transportState, {
        state,
        direction: transport.direction,
      });

      if (['disconnected', 'failed'].includes(state)) {
        if (this.#iceIntervalTimeouts.has(transport.id)) {
          return;
        }

        if (state === 'failed') {
          this.#logger.debug(`${ transport.id } transport.restartIce()`, {
            transportId: transport.id,
            direction: transport.direction,
          });
          this.restartIce(transport).catch((error: unknown) => {
            this.#logger.debug('Failed to execute restartIce', {
              error: serializeError(error),
              transportId: transport.id,
              direction: transport.direction,
            });
          });
        }

        this.#iceIntervalTimeouts.set(transport.id, setInterval(() => {
          this.restartIce(transport).catch((error: unknown) => {
            this.#logger.debug('Failed to execute restartIce', {
              error: serializeError(error),
              transportId: transport.id,
              direction: transport.direction,
            });
          });
          this.#logger.debug(`${ transport.id } transport.restartIce() attempt`, {
            recvTransportId: transport.id,
            transportId: transport.id,
            direction: transport.direction,
          });
        }, TRANSPORT_RESTART_ICE_INTERVAL));
      }
    });

    transport.on('icegatheringstatechange', (state: RTCIceGathererState) => {
      this.#logger.info('icegatheringstatechange', {
        state,
        transportId: transport.id,
        transportDirection: transport.direction,
      });
    });
  }

  private async checkTransportState(transport: LDTransport): Promise<void> {
    try {
      const stats = await this.getParsedTransportStats(transport);

      if (!stats) {
        return;
      }

      const direction = transport.direction === 'send' ? 'send' : 'receive';

      if (stats.iceState !== 'connected') {
        this.clientEventEmitter.emit(CLIENT_EVENTS.transportConnectionTimeout, {
          direction,
          reason: 'ice',
        });

        this.#logger.warn(MEDIASOUP_EVENTS.transportConnectionTimeout, {
          reason: 'ice',
          transportId: transport.id,
          direction,
        });

        return;
      }

      if (stats.dtlsState !== 'connected') {
        this.clientEventEmitter.emit(CLIENT_EVENTS.transportConnectionTimeout, {
          direction,
          reason: 'dtls',
        });

        this.#logger.warn(MEDIASOUP_EVENTS.transportConnectionTimeout, {
          reason: 'dtls',
          transportId: transport.id,
          direction,
        });
      }
    } catch (error) {
      this.#logger.error('checkTransportState()', {
        error: serializeError(error),
        transportId: transport.id,
        direction: transport.direction,
      });
    }
  }
}

export default Network;
